import type { Position } from '../types';

/**
 * Unit direction vector in [lat, lng] space (L.CRS.Simple). Matches the
 * coordinate order used on `Position.coordinates` everywhere else.
 */
export type Direction = [number, number];

const HORIZONTAL: Direction = [0, 1];

/**
 * Return the major-axis unit direction of `points` via a 2×2 PCA on their
 * [lat, lng] coordinates. This is the "street direction" a user-selected
 * row/column of markers is pointing in — works for horizontal, vertical,
 * and arbitrary diagonal lines. Degenerate inputs (<2 points, coincident
 * points) default to horizontal.
 *
 * Mixed-type selections are de-trended per type before PCA: each type's
 * centroid is subtracted from its members so the perpendicular gap
 * between staggered per-type lanes doesn't pollute the variance
 * calculation. Without this, a row whose lane gap exceeds the along-row
 * spread (common when only part of a row is selected) can flip the
 * dominant axis 90°. Single-member type groups skip de-trending — there
 * is no within-group spread to preserve, and zeroing their position
 * would discard the only information they carry.
 */
export function computeBestFitDirection(points: Position[]): Direction {
  const n = points.length;
  if (n < 2) return HORIZONTAL;

  const byType = groupByType(points);
  // De-trend types with 2+ members (subtract per-type centroid). Single-
  // member types fall back to the global mean below so their raw position
  // still informs the direction without dominating it.
  const centered: Array<[number, number]> = [];
  const singletons: Position[] = [];
  for (const group of byType.values()) {
    if (group.length >= 2) {
      let mLat = 0;
      let mLng = 0;
      for (const p of group) {
        mLat += p.coordinates[0];
        mLng += p.coordinates[1];
      }
      mLat /= group.length;
      mLng /= group.length;
      for (const p of group) {
        centered.push([p.coordinates[0] - mLat, p.coordinates[1] - mLng]);
      }
    } else {
      singletons.push(group[0]!);
    }
  }
  if (singletons.length > 0) {
    // Center singletons against their own collective mean so they
    // contribute their relative spread (not absolute position).
    let mLat = 0;
    let mLng = 0;
    for (const p of singletons) {
      mLat += p.coordinates[0];
      mLng += p.coordinates[1];
    }
    mLat /= singletons.length;
    mLng /= singletons.length;
    for (const p of singletons) {
      centered.push([p.coordinates[0] - mLat, p.coordinates[1] - mLng]);
    }
  }

  // Covariance matrix entries: Sll = var(lat), Snn = var(lng), Sln = cov(lat, lng).
  let Sll = 0;
  let Snn = 0;
  let Sln = 0;
  for (const [dl, dn] of centered) {
    Sll += dl * dl;
    Snn += dn * dn;
    Sln += dl * dn;
  }
  if (Sll + Snn < 1e-9) return HORIZONTAL;

  // Larger eigenvalue of [[Sll, Sln], [Sln, Snn]] — its eigenvector is the major axis.
  const trace = Sll + Snn;
  const det = Sll * Snn - Sln * Sln;
  const disc = Math.sqrt(Math.max(0, (trace * trace) / 4 - det));
  const lambda = trace / 2 + disc;

  let vLat: number;
  let vLng: number;
  if (Math.abs(Sln) > 1e-9) {
    // Eigenvector for λ: [b, λ - a] = [Sln, λ - Sll]
    vLat = Sln;
    vLng = lambda - Sll;
  } else {
    // Sln ≈ 0 → axes are already eigenvectors; pick the dominant one.
    if (Sll >= Snn) { vLat = 1; vLng = 0; }
    else { vLat = 0; vLng = 1; }
  }
  const len = Math.hypot(vLat, vLng);
  if (len < 1e-9) return HORIZONTAL;
  return [vLat / len, vLng / len];
}

/**
 * Snap every selected position onto a single shared line, regardless of
 * type. The line is anchored on the two members that are farthest apart
 * along `direction` — those stay at their current positions and define
 * the segment. Interior members keep their along-axis projection but
 * snap perpendicular onto the segment, with the perpendicular offset
 * linearly interpolated between the two endpoints (so end-to-end drift
 * in the row is preserved).
 *
 * Returns no updates when fewer than 3 markers are selected (1 has
 * nothing to align; 2 already define their own line). When all members
 * are coincident along the axis (no endpoint pair to anchor on), falls
 * back to snapping them to the group's mean perpendicular offset.
 */
export function alignToLine(
  positions: Position[],
  direction: Direction,
): Map<string, [number, number]> {
  // Perpendicular direction in [lat, lng] (90° rotation preserves unit length).
  const [uLat, uLng] = direction;
  const nLat = -uLng;
  const nLng = uLat;

  const updates = new Map<string, [number, number]>();
  if (positions.length < 3) return updates;

  const projected = positions.map(p => ({
    p,
    along: p.coordinates[0] * uLat + p.coordinates[1] * uLng,
    across: p.coordinates[0] * nLat + p.coordinates[1] * nLng,
  }));
  let minIdx = 0;
  let maxIdx = 0;
  for (let i = 1; i < projected.length; i++) {
    if (projected[i]!.along < projected[minIdx]!.along) minIdx = i;
    if (projected[i]!.along > projected[maxIdx]!.along) maxIdx = i;
  }
  const start = projected[minIdx]!;
  const end = projected[maxIdx]!;
  const dAlong = end.along - start.along;

  if (Math.abs(dAlong) < 1e-9) {
    // All members coincident along the axis — no endpoint pair to
    // anchor on. Fall back to mean-perpendicular collapse so the group
    // still ends up on a single line.
    const meanAcross =
      projected.reduce((s, x) => s + x.across, 0) / projected.length;
    for (const x of projected) {
      const newLat = x.along * uLat + meanAcross * nLat;
      const newLng = x.along * uLng + meanAcross * nLng;
      updates.set(x.p.uid, [newLat, newLng]);
    }
    return updates;
  }

  const dAcross = end.across - start.across;
  for (const x of projected) {
    // Endpoints stay put (they define the line). Interior members snap
    // onto the segment, perpendicular offset linearly interpolated.
    const t = (x.along - start.along) / dAlong;
    const targetAcross = start.across + t * dAcross;
    const newLat = x.along * uLat + targetAcross * nLat;
    const newLng = x.along * uLng + targetAcross * nLng;
    updates.set(x.p.uid, [newLat, newLng]);
  }
  return updates;
}

/**
 * Evenly space every selected marker along a single shared line. The
 * line is anchored on the two members farthest apart along `direction`
 * (those stay put as the segment endpoints); the remaining members are
 * placed at equal along-axis intervals between them, with their
 * perpendicular offset linearly interpolated onto the line.
 *
 * Returns no updates when fewer than 3 markers are selected (with 2,
 * the segment is already "evenly spaced" by definition). Returns no
 * updates when all members share the same along-axis projection — the
 * step would collapse them onto a single point.
 */
export function distributeAlongLine(
  positions: Position[],
  direction: Direction,
): Map<string, [number, number]> {
  const [uLat, uLng] = direction;
  const nLat = -uLng;
  const nLng = uLat;

  const updates = new Map<string, [number, number]>();
  if (positions.length < 3) return updates;

  const projected = positions.map(p => ({
    p,
    along: p.coordinates[0] * uLat + p.coordinates[1] * uLng,
    across: p.coordinates[0] * nLat + p.coordinates[1] * nLng,
  }));
  projected.sort((a, b) => a.along - b.along);
  const first = projected[0]!;
  const last = projected[projected.length - 1]!;
  const dAlong = last.along - first.along;
  if (Math.abs(dAlong) < 1e-9) return updates;

  const dAcross = last.across - first.across;
  const step = dAlong / (projected.length - 1);
  projected.forEach((item, i) => {
    const t = i / (projected.length - 1);
    const along = first.along + step * i;
    const targetAcross = first.across + t * dAcross;
    const newLat = along * uLat + targetAcross * nLat;
    const newLng = along * uLng + targetAcross * nLng;
    updates.set(item.p.uid, [newLat, newLng]);
  });
  return updates;
}

function groupByType(positions: Position[]): Map<Position['type'], Position[]> {
  const byType = new Map<Position['type'], Position[]>();
  for (const p of positions) {
    const g = byType.get(p.type);
    if (g) g.push(p);
    else byType.set(p.type, [p]);
  }
  return byType;
}
